import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'
import { DriverPayGroup } from 'store/driver-pay/driver-pay.types'

type DriverPayPreviewDialogProps = {
  open: boolean
  loading: boolean
  generating: boolean
  group: DriverPayGroup | null
  onClose: () => void
  onGenerateInvoice: () => void
}

const formatRate = (rate: number) =>
  `$${rate.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const formatAmount = (amount: number) =>
  `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const formatMiles = (miles: number) =>
  miles.toLocaleString('en-US', {
    minimumFractionDigits: miles % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })

const DriverPayPreviewDialog = ({
  open,
  loading,
  generating,
  group,
  onClose,
  onGenerateInvoice,
}: DriverPayPreviewDialogProps) => {
  const trips = group?.trips ?? []

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        Invoice Preview
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading || !group ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'action.hover',
              }}
            >
              <PreviewStat label="Week #" value={String(group.week)} />
              <PreviewStat label="Driver Name" value={group.driverName || '—'} />
              <PreviewStat label="Invoice #" value={group.invoiceNumber} />
              <PreviewStat
                label="Rate"
                value={`${formatRate(group.rate)}/mile`}
              />
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Trip #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Miles</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No trips in this group
                    </TableCell>
                  </TableRow>
                ) : (
                  trips.map((trip) => (
                    <TableRow key={trip.userTripId}>
                      <TableCell>{trip.userTripId}</TableCell>
                      <TableCell>{trip.date || '—'}</TableCell>
                      <TableCell align="right">
                        {formatMiles(trip.miles)}
                      </TableCell>
                      <TableCell align="right">
                        {formatAmount(trip.amount)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <Stack
              direction="row"
              justifyContent="space-around"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'action.hover',
              }}
            >
              <PreviewStat
                label="Total Trips"
                value={String(group.tripCount ?? trips.length)}
              />
              <PreviewStat
                label="Total Miles"
                value={formatMiles(group.miles)}
              />
              <PreviewStat
                label="Total Amount"
                value={formatAmount(group.amount)}
                emphasize
              />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <SubmitLockButton
          variant="contained"
          onClick={onGenerateInvoice}
          disabled={loading || !group || generating}
        >
          {generating ? 'Generating…' : 'Generate Invoice'}
        </SubmitLockButton>
      </DialogActions>
    </Dialog>
  )
}

const PreviewStat = ({
  label,
  value,
  emphasize,
}: {
  label: string
  value: string
  emphasize?: boolean
}) => (
  <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="subtitle1"
      sx={{
        fontWeight: 700,
        color: emphasize ? 'primary.main' : 'text.primary',
      }}
    >
      {value}
    </Typography>
  </Stack>
)

export default DriverPayPreviewDialog
